// Actions on a system the player does not own (spec §3.4): scout and claim in sheets, send a fleet.
import type { ClaimantClaim, GalaxyMeResponse } from '@stars-and-ships/api-types';
import { useMutation } from '@tanstack/react-query';
import { Flag, Radar, Rocket } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router';
import { endpoints } from '../../api/endpoints';
import { ApiRequestError } from '../../api/http';
import { useClaimQuote, useClaims, useScouts, useSystem } from '../../api/queries';
import { formatDuration, formatNumber } from '../../labels';
import {
  CancelClaimButton,
  CLAIM_STATUS_LABELS,
  claimTiming,
  OPEN_CLAIM,
  until,
  useInvalidateExpansion,
} from '../claims';
import { Button, ErrorNotice, Notice, Select, Sheet, TextField } from '../ui';

function ScoutForm({
  galaxyId,
  me,
  systemId,
  onSent,
}: {
  galaxyId: string;
  me: GalaxyMeResponse;
  systemId: string;
  onSent(cached: boolean): void;
}) {
  const refresh = useInvalidateExpansion(galaxyId);
  const [originChoice, setOrigin] = useState('');
  const originId = originChoice || me.systems[0]?.id || '';
  const origin = useSystem(galaxyId, originId || undefined);
  const stored = origin.data?.tier === 'full' ? origin.data.system.influence.stored : undefined;
  const [amount, setAmount] = useState('');
  const send = useMutation({
    mutationFn: () =>
      endpoints.sendScout(galaxyId, {
        originSystemId: originId,
        targetSystemId: systemId,
        influence: Number(amount),
      }),
    onSuccess: (response) => {
      refresh();
      onSent(!('scout' in response));
    },
  });
  return (
    <form
      data-scout-form
      className="space-y-3"
      onSubmit={(e) => {
        e.preventDefault();
        send.mutate();
      }}
    >
      <p className="text-sm text-muted">
        A scout carries Influence from an Embassy. It succeeds if it carries more than the target
        stores; unowned systems always give way.
      </p>
      <Select label="Scout from" value={originId} onChange={(e) => setOrigin(e.target.value)}>
        {me.systems.map((s) => (
          <option key={s.id} value={s.id}>
            {s.name}
          </option>
        ))}
      </Select>
      <TextField
        label="Influence to carry"
        hint={stored !== undefined ? `Stored: ${formatNumber(stored)}` : undefined}
        inputMode="numeric"
        value={amount}
        onChange={(e) => setAmount(e.target.value.replace(/\D/g, ''))}
      />
      {send.error ? <ErrorNotice error={send.error} /> : null}
      <Button
        type="submit"
        className="w-full"
        disabled={!amount || !originId}
        pending={send.isPending}
      >
        <Radar className="size-4" aria-hidden /> Send scout
      </Button>
    </form>
  );
}

function ClaimForm({
  galaxyId,
  me,
  systemId,
  open,
  onDone,
}: {
  galaxyId: string;
  me: GalaxyMeResponse;
  systemId: string;
  open: ClaimantClaim | undefined;
  onDone(): void;
}) {
  const refresh = useInvalidateExpansion(galaxyId);
  const quote = useClaimQuote(galaxyId, systemId, !open);
  const place = useMutation({
    mutationFn: (expectedCostCredits: number) =>
      endpoints.placeClaim(galaxyId, { systemId, expectedCostCredits }),
    onSuccess: () => {
      refresh();
      onDone();
    },
    onError: (e) => {
      if (e instanceof ApiRequestError && e.code === 'COST_CHANGED') void quote.refetch();
    },
  });
  const cancel = useMutation({
    mutationFn: (claimId: string) => endpoints.cancelClaim(galaxyId, claimId),
    onSuccess: () => {
      refresh();
      onDone();
    },
  });
  if (open) {
    return (
      <div className="space-y-3">
        <p className="text-sm text-muted">
          {open.status === 'active'
            ? 'Win a battle here (or arrive at an undefended system) to capture it.'
            : 'Once active, win a battle here to capture the system.'}
        </p>
        {cancel.error ? <ErrorNotice error={cancel.error} /> : null}
        <CancelClaimButton
          claim={open}
          label="Cancel claim"
          disabled={cancel.isPending}
          onConfirm={() => cancel.mutate(open.id)}
        />
      </div>
    );
  }
  if (quote.error) return <ErrorNotice error={quote.error} />;
  if (!quote.data) return null;
  const short = me.credits < quote.data.costCredits;
  return (
    <div className="space-y-3">
      <p className="text-sm text-muted">
        Claiming costs{' '}
        <span className="font-mono tabular-nums text-credits" data-claim-cost>
          {formatNumber(quote.data.costCredits)}
        </span>{' '}
        Credits (claim #{quote.data.claimWeightN}). It activates{' '}
        {formatDuration(quote.data.activatesAfterMs)} after you place it and expires after{' '}
        {formatDuration(quote.data.expiresAfterMs)}.
      </p>
      {place.error ? <ErrorNotice error={place.error} /> : null}
      <Button
        className="w-full"
        disabled={short}
        pending={place.isPending}
        onClick={() => quote.data && place.mutate(quote.data.costCredits)}
      >
        <Flag className="size-4" aria-hidden /> Place claim
      </Button>
      {short ? (
        <p className="text-xs text-faint">Trade resources for Credits at a Trading Port.</p>
      ) : null}
    </div>
  );
}

export function ForeignActions({
  galaxyId,
  me,
  systemId,
  now,
}: {
  galaxyId: string;
  me: GalaxyMeResponse | undefined;
  systemId: string;
  now: number;
}) {
  const [sheet, setSheet] = useState<'scout' | 'claim' | null>(null);
  const [cachedReport, setCachedReport] = useState(false);
  const scouts = useScouts(galaxyId);
  const mine = useClaims(galaxyId, 'mine');
  if (!me || me.systems.length === 0) return null;
  const scout = scouts.data?.items.find(
    (s) => s.targetSystemId === systemId && s.status === 'in_transit'
  );
  const open = mine.data?.items.find(
    (c): c is ClaimantClaim =>
      c.view === 'claimant' && c.systemId === systemId && OPEN_CLAIM.has(c.status)
  );
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2">
        <Button variant="secondary" onClick={() => setSheet('scout')}>
          <Radar className="size-4" aria-hidden /> Scout
        </Button>
        <Button variant="secondary" disabled={!mine.data} onClick={() => setSheet('claim')}>
          <Flag className="size-4" aria-hidden /> {open ? 'Your claim' : 'Claim'}
        </Button>
        <Link
          to={`/g/${galaxyId}/fleet/send?target=${systemId}`}
          className="inline-flex min-h-11 items-center gap-2 hud-cut-sm bg-accent px-4 font-display text-sm font-bold uppercase tracking-[0.12em] text-accent-ink hover:bg-accent-strong"
        >
          <Rocket className="size-4" aria-hidden /> Send fleet here
        </Link>
      </div>
      {scout ? (
        <p data-scout-status className="text-sm text-info">
          Scout on its way — arrives {until(scout.arrivesAt, now)}.
        </p>
      ) : null}
      {cachedReport ? (
        <Notice tone="success">You scouted this system moments ago; that report is shown.</Notice>
      ) : null}
      {open ? (
        <p data-own-claim={open.status} className="text-sm text-text">
          Your claim is <strong data-claim-status>{CLAIM_STATUS_LABELS[open.status]}</strong> —{' '}
          {claimTiming(open, now)}.
        </p>
      ) : null}
      <Sheet open={sheet === 'scout'} title="Scout" onClose={() => setSheet(null)}>
        <ScoutForm
          galaxyId={galaxyId}
          me={me}
          systemId={systemId}
          onSent={(cached) => {
            setCachedReport(cached);
            setSheet(null);
          }}
        />
      </Sheet>
      <Sheet
        open={sheet === 'claim'}
        title={open ? 'Your claim' : 'Claim'}
        onClose={() => setSheet(null)}
      >
        <ClaimForm
          galaxyId={galaxyId}
          me={me}
          systemId={systemId}
          open={open}
          onDone={() => setSheet(null)}
        />
      </Sheet>
    </div>
  );
}
